import { cn, tv, VariantProps } from "@utils/tw"
import { Link } from "eddev/routing"
import { forwardRef, ReactElement, useContext } from "react"
import { ThemedContext } from "./Themed"

export type ButtonProps = VariantProps<typeof buttonStyles> & {
  icon?: ReactElement | null
  iconAlign?: "left" | "right"
  href?: string
  target?: string
  className?: string
  style?: React.CSSProperties
  outerClassName?: string
  children?: React.ReactNode
  onClick?: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void
  type?: "button" | "submit" | "reset"
  size?: "default" | "sm"
}

export const Button = forwardRef<HTMLDivElement, ButtonProps>((props, ref) => {
  const { buttonVariant } = useContext(ThemedContext)
  const styles = buttonStyles({ ...props, variant: props.variant || buttonVariant })
  const { iconAlign = "left", type, outerClassName, ...restProps } = props
  const iconAlignVal = iconAlign ?? "left"
  const Element = props.href ? Link : "button"
  const elProps = props.href
    ? {
        href: props.href,
        target: props.target,
      }
    : {
        type: props.type ?? "button",
      }

  return (
    <div className={cn("flex", outerClassName)} ref={ref}>
      <Element {...restProps} {...elProps} className={cn(styles.base(), props.className)} style={props.style}>
        {props.icon && iconAlignVal === "left" && <span className={styles.icon()}>{props.icon}</span>}
        <span className={styles.label()}>{props.children}</span>
        {props.icon && iconAlignVal === "right" && <span className={styles.icon()}>{props.icon}</span>}
      </Element>
    </div>
  )
})

export const buttonStyles = tv({
  base: [
    "inline-flex w-fit items-center justify-center outline-none cursor-pointer no-underline",
    "transition-all duration-300",
  ],
  slots: {
    label: "",
    icon: "",
  },
  variants: {
    variant: {
      primary:
        "bg-transparent border border-current md:hover:bg-black md:hover:text-white md:hover:border-black active:bg-black/80 active:text-white", // dark
      light:
        "bg-transparent text-white border border-current md:hover:bg-white md:hover:text-black md:hover:border-white active:bg-white/80 active:text-black", // light
      inverted: "bg-black text-white hover:bg-black/80 active:bg-black/60 border border-current",
    },
    size: {
      default: "px-5 h-button type-label-m",
      sm: "px-3 h-buttonChip type-label-s",
    },
    radius: {
      sm: "rounded-sm hover:rounded-md",
      md: "rounded-md hover:rounded-sm",
      lg: "rounded-[calc(var(--size-button)*0.5)] hover:rounded-sm",
    },
  },
  defaultVariants: {
    variant: "primary",
    radius: "lg",
    size: "default",
  },
})
